import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { Json } from '../types/supabase';

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  image_url?: string;
  category?: string;
  tags?: string[];
  sku?: string;
  stock?: number;
  position: number;
  source: 'manual' | 'csv' | 'shopify' | 'prestashop';
  metadata?: Record<string, unknown>;
  discount?: number;
}

interface ProductSettings {
  displayMode: 'grid' | 'list' | 'carousel';
  gridColumns: number;
  spacing: number;
  showPrices: boolean;
  showStock: boolean;
  currencyDisplay: string;
}

interface ProductSystemStore {
  products: Product[];
  categories: { id: string; name: string; description?: string }[];
  tags: { id: string; name: string }[];
  settings: ProductSettings;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  initialize: (chatbotPublicId: string) => Promise<void>;
  addProduct: (chatbotPublicId: string, product: Omit<Product, 'id' | 'position'>) => Promise<void>;
  updateProduct: (chatbotPublicId: string, productId: string, updates: Partial<Product>) => Promise<void>;
  deleteProduct: (chatbotPublicId: string, productId: string) => Promise<void>;
  deleteAllProducts: (chatbotPublicId: string) => Promise<void>;
  reorderProducts: (chatbotPublicId: string, productIds: string[]) => Promise<void>;
  addCategory: (chatbotPublicId: string, category: { name: string; description?: string }) => Promise<void>;
  updateCategory: (chatbotPublicId: string, categoryId: string, updates: Partial<{ name: string; description: string }>) => Promise<void>;
  deleteCategory: (chatbotPublicId: string, categoryId: string) => Promise<void>;
  addTag: (chatbotPublicId: string, name: string) => Promise<void>;
  updateTag: (chatbotPublicId: string, tagId: string, name: string) => Promise<void>;
  deleteTag: (chatbotPublicId: string, tagId: string) => Promise<void>;
  importFromCSV: (chatbotPublicId: string, products: Omit<Product, 'id' | 'position'>[]) => Promise<void>;
  updateSettings: (chatbotPublicId: string, settings: Partial<ProductSettings>) => Promise<void>;
}

const DEFAULT_SETTINGS: ProductSettings = {
  displayMode: 'grid',
  gridColumns: 3,
  spacing: 4,
  showPrices: true,
  showStock: true,
  currencyDisplay: '€'
};

export const useProductSystem = create<ProductSystemStore>((set, get) => ({
  products: [],
  categories: [],
  tags: [],
  settings: DEFAULT_SETTINGS,
  loading: false,
  error: null,
  initialized: false,

  initialize: async (chatbotPublicId) => {
    if (!chatbotPublicId) {
      set({ error: 'Chatbot ID is required', loading: false });
      return;
    }

    try {
      set({ loading: true, error: null });

      // Fetch product settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('product_settings')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError;
      }

      // Fetch products
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('position');

      if (productsError) throw productsError;

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('product_categories')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('name');

      if (categoriesError) throw categoriesError;

      // Fetch tags
      const { data: tagsData, error: tagsError } = await supabase
        .from('product_tags')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('name');

      if (tagsError) throw tagsError;

      // Map products to the Product interface
      const mappedProducts: Product[] = productsData?.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description || undefined,
        price: p.price || 0,
        currency: p.currency || 'EUR',
        image_url: p.image_url || undefined,
        category: p.category || undefined,
        tags: p.tags || [],
        sku: p.sku || undefined,
        stock: p.stock || 0,
        position: p.position || 0,
        source: (p.source || 'manual') as 'manual' | 'csv' | 'shopify' | 'prestashop',
        metadata: p.metadata as Record<string, unknown> || {},
        discount: p.discount || 0
      })) || [];

      // Map settings to the ProductSettings interface
      const mappedSettings: ProductSettings = {
        displayMode: settingsData?.display_mode || DEFAULT_SETTINGS.displayMode,
        gridColumns: settingsData?.grid_columns || DEFAULT_SETTINGS.gridColumns,
        spacing: settingsData?.spacing || DEFAULT_SETTINGS.spacing,
        showPrices: settingsData?.show_prices ?? DEFAULT_SETTINGS.showPrices,
        showStock: settingsData?.show_stock ?? DEFAULT_SETTINGS.showStock,
        currencyDisplay: settingsData?.currency_display || DEFAULT_SETTINGS.currencyDisplay
      };

      // Map categories and tags
      const mappedCategories = categoriesData?.map(cat => ({
        id: cat.id,
        name: cat.name,
        description: cat.description || undefined
      })) || [];

      const mappedTags = tagsData?.map(tag => ({
        id: tag.id,
        name: tag.name
      })) || [];

      set({
        settings: mappedSettings,
        products: mappedProducts,
        categories: mappedCategories,
        tags: mappedTags,
        loading: false,
        initialized: true
      });
    } catch (err) {
      console.error('Error initializing product system:', err);
      set({
        settings: DEFAULT_SETTINGS,
        error: err instanceof Error ? err.message : 'Error initializing product system',
        loading: false,
        initialized: true
      });
    }
  },

  addProduct: async (chatbotPublicId, product) => {
    try {
      set({ loading: true, error: null });

      const maxPosition = Math.max(0, ...get().products.map(p => p.position));

      const { error } = await supabase
        .from('products')
        .insert({
          chatbot_public_id: chatbotPublicId,
          name: product.name,
          description: product.description || null,
          price: product.price,
          currency: product.currency || 'EUR',
          image_url: product.image_url || null,
          category: product.category || null,
          tags: product.tags || [],
          sku: product.sku || null,
          stock: product.stock || 0,
          source: product.source || 'manual',
          external_id: '', // This will be set by the trigger
          metadata: product.metadata as Json || {},
          discount: product.discount || 0,
          position: maxPosition + 1
        });

      if (error) throw error;

      // Refresh products
      await get().initialize(chatbotPublicId);
    } catch (err) {
      console.error('Error adding product:', err);
      set({ error: err instanceof Error ? err.message : 'Error adding product' });
    } finally {
      set({ loading: false });
    }
  },

  updateProduct: async (chatbotPublicId, productId, updates) => {
    try {
      set({ loading: true, error: null });

      // Transform the updates to match the database schema
      const dbUpdates: Record<string, any> = {};
      
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.description !== undefined) dbUpdates.description = updates.description || null;
      if (updates.price !== undefined) dbUpdates.price = updates.price;
      if (updates.currency !== undefined) dbUpdates.currency = updates.currency || 'EUR';
      if (updates.image_url !== undefined) dbUpdates.image_url = updates.image_url || null;
      if (updates.category !== undefined) dbUpdates.category = updates.category || null;
      if (updates.tags !== undefined) dbUpdates.tags = updates.tags || [];
      if (updates.sku !== undefined) dbUpdates.sku = updates.sku || null;
      if (updates.stock !== undefined) dbUpdates.stock = updates.stock || 0;
      if (updates.position !== undefined) dbUpdates.position = updates.position;
      if (updates.source !== undefined) dbUpdates.source = updates.source;
      if (updates.discount !== undefined) dbUpdates.discount = updates.discount || 0;
      if (updates.metadata !== undefined) dbUpdates.metadata = updates.metadata as Json || {};

      const { error } = await supabase
        .from('products')
        .update(dbUpdates)
        .eq('id', productId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      // Refresh products
      await get().initialize(chatbotPublicId);
    } catch (err) {
      console.error('Error updating product:', err);
      set({ error: err instanceof Error ? err.message : 'Error updating product' });
    } finally {
      set({ loading: false });
    }
  },

  deleteProduct: async (chatbotPublicId, productId) => {
    try {
      set({ loading: true, error: null });

      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      set(state => ({
        products: state.products.filter(p => p.id !== productId)
      }));
    } catch (err) {
      console.error('Error deleting product:', err);
      set({ error: err instanceof Error ? err.message : 'Error deleting product' });
    } finally {
      set({ loading: false });
    }
  },

  deleteAllProducts: async (chatbotPublicId) => {
    try {
      set({ loading: true, error: null });

      // Confirmer que nous avons un ID de chatbot valide pour éviter la suppression accidentelle de tous les produits
      if (!chatbotPublicId) {
        throw new Error('Chatbot ID is required to delete all products');
      }

      const { error } = await supabase
        .from('products')
        .delete()
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      // Mettre à jour l'état pour refléter que tous les produits ont été supprimés
      set(state => ({
        ...state,
        products: []
      }));

      console.log(`All products deleted for chatbot: ${chatbotPublicId}`);
    } catch (err) {
      console.error('Error deleting all products:', err);
      set({ error: err instanceof Error ? err.message : 'Error deleting all products' });
    } finally {
      set({ loading: false });
    }
  },

  reorderProducts: async (chatbotPublicId, productIds) => {
    try {
      set({ loading: true, error: null });

      // Update positions
      for (let i = 0; i < productIds.length; i++) {
        const { error } = await supabase
          .from('products')
          .update({ position: i })
          .eq('id', productIds[i])
          .eq('chatbot_public_id', chatbotPublicId);

        if (error) throw error;
      }

      // Refresh products
      await get().initialize(chatbotPublicId);
    } catch (err) {
      console.error('Error reordering products:', err);
      set({ error: err instanceof Error ? err.message : 'Error reordering products' });
    } finally {
      set({ loading: false });
    }
  },

  addCategory: async (chatbotPublicId, category) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('product_categories')
        .insert({
          chatbot_public_id: chatbotPublicId,
          ...category
        })
        .select('*');

      if (error) throw error;

      // Update categories in state
      const currentState = get();
      set({ 
        categories: [...currentState.categories, data[0]],
        loading: false 
      });
    } catch (err) {
      console.error('Error adding category:', err);
      set({ error: err instanceof Error ? err.message : 'Error adding category', loading: false });
    }
  },

  updateCategory: async (chatbotPublicId, categoryId, updates) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('product_categories')
        .update(updates)
        .eq('id', categoryId)
        .eq('chatbot_public_id', chatbotPublicId)
        .select('*');

      if (error) throw error;

      // Update category in state
      const currentState = get();
      set({ 
        categories: currentState.categories.map(cat => 
          cat.id === categoryId ? data[0] : cat
        ),
        loading: false 
      });
    } catch (err) {
      console.error('Error updating category:', err);
      set({ error: err instanceof Error ? err.message : 'Error updating category', loading: false });
    }
  },

  deleteCategory: async (chatbotPublicId, categoryId) => {
    try {
      set({ loading: true, error: null });

      // First, update products to remove this category
      await supabase
        .from('products')
        .update({ category: null })
        .eq('category', categoryId)
        .eq('chatbot_public_id', chatbotPublicId);

      // Then delete the category
      const { error } = await supabase
        .from('product_categories')
        .delete()
        .eq('id', categoryId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      // Update state directly
      const currentState = get();
      set({ 
        categories: currentState.categories.filter(cat => cat.id !== categoryId),
        loading: false 
      });
    } catch (err) {
      console.error('Error deleting category:', err);
      set({ error: err instanceof Error ? err.message : 'Error deleting category', loading: false });
    }
  },

  addTag: async (chatbotPublicId, name) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('product_tags')
        .insert({
          chatbot_public_id: chatbotPublicId,
          name
        })
        .select('*');

      if (error) throw error;

      // Instead of calling initialize, directly update the tags state
      const currentState = get();
      set({ 
        tags: [...currentState.tags, data[0]],
        loading: false 
      });
    } catch (err) {
      console.error('Error adding tag:', err);
      set({ error: err instanceof Error ? err.message : 'Error adding tag', loading: false });
    }
  },

  updateTag: async (chatbotPublicId, tagId, name) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('product_tags')
        .update({ name })
        .eq('id', tagId)
        .eq('chatbot_public_id', chatbotPublicId)
        .select('*');

      if (error) throw error;

      // Update tag in the current state
      const currentState = get();
      set({ 
        tags: currentState.tags.map(tag => tag.id === tagId ? data[0] : tag),
        loading: false 
      });
    } catch (err) {
      console.error('Error updating tag:', err);
      set({ error: err instanceof Error ? err.message : 'Error updating tag', loading: false });
    }
  },

  deleteTag: async (chatbotPublicId, tagId) => {
    try {
      set({ loading: true, error: null });

      // First, update products to remove this tag from their tags array
      const { data: products } = await supabase
        .from('products')
        .select('id, tags')
        .eq('chatbot_public_id', chatbotPublicId);

      if (products) {
        for (const product of products) {
          if (product.tags && product.tags.includes(tagId)) {
            await supabase
              .from('products')
              .update({
                tags: product.tags.filter((id: string) => id !== tagId)
              })
              .eq('id', product.id)
              .eq('chatbot_public_id', chatbotPublicId);
          }
        }
      }

      // Then delete the tag
      const { error } = await supabase
        .from('product_tags')
        .delete()
        .eq('id', tagId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      // Update tags in state
      const currentState = get();
      set({ 
        tags: currentState.tags.filter(tag => tag.id !== tagId),
        loading: false 
      });
    } catch (err) {
      console.error('Error deleting tag:', err);
      set({ error: err instanceof Error ? err.message : 'Error deleting tag', loading: false });
    }
  },

  importFromCSV: async (chatbotPublicId, products) => {
    try {
      set({ loading: true, error: null });

      const { error } = await supabase.rpc('import_products_from_csv', {
        p_chatbot_public_id: chatbotPublicId,
        p_products: products as Json
      });

      if (error) throw error;

      await get().initialize(chatbotPublicId);
    } catch (err) {
      console.error('Error importing products:', err);
      set({ error: err instanceof Error ? err.message : 'Error importing products' });
    } finally {
      set({ loading: false });
    }
  },

  updateSettings: async (chatbotPublicId, settings) => {
    try {
      set({ loading: true, error: null });

      // Vérifier si un enregistrement existe déjà
      const { data, error: checkError } = await supabase
        .from('product_settings')
        .select('id')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') throw checkError;

      // Préparer les données pour la base de données
      const dbSettings: Record<string, any> = {
        ...(settings.displayMode !== undefined && { display_mode: settings.displayMode }),
        ...(settings.gridColumns !== undefined && { grid_columns: settings.gridColumns }),
        ...(settings.spacing !== undefined && { spacing: settings.spacing }),
        ...(settings.showPrices !== undefined && { show_prices: settings.showPrices }),
        ...(settings.showStock !== undefined && { show_stock: settings.showStock }),
        ...(settings.currencyDisplay !== undefined && { currency_display: settings.currencyDisplay })
      };

      if (data) {
        // Mettre à jour les paramètres existants
        const { error } = await supabase
          .from('product_settings')
          .update(dbSettings)
          .eq('id', data.id);

        if (error) throw error;
      } else {
        // Insérer de nouveaux paramètres
        const { error } = await supabase
          .from('product_settings')
          .insert({
            chatbot_public_id: chatbotPublicId,
            ...dbSettings
          });

        if (error) throw error;
      }

      // Update local state
      set(state => ({
        settings: {
          ...state.settings,
          ...settings
        }
      }));
    } catch (err) {
      console.error('Error updating settings:', err);
      set({ error: err instanceof Error ? err.message : 'Error updating settings' });
    } finally {
      set({ loading: false });
    }
  }
}));